import { useMemo } from "react";
import TableBody_team from "./TableBody_team";
import type { CellViolationInfo } from "@/services/validation";
import LoadingSpiner from "@/components/ui/LoadingSpiner";

import type * as type from "@vTypes";
import { useRosterDisplayShiftColumns } from "@/hooks/shift/useRosterDisplayShiftColumns";
import { useRosterSubmissionQuery } from "@hooks/roster-craete/useRosterSubmissionQuery";
import { useScheduleStore } from "@store/scheduleStore";
import { useIsAdmin } from "@hooks/useAuth.ts";
import { useGroupStore } from "@store/groupStore.ts";
import useRosterConfig from "@/hooks/useRosterConfig";
import { useShiftsQuery } from "@/hooks/roster-craete/useShiftsQuery";
import { useShiftLookupMaps } from "@/hooks/shift/useShiftLookupMaps";
import { useTeamsQuery } from "@/hooks/roster-craete/useTeamsQuery";
import useGroupMembers from "@/hooks/useGroupMembers";
import { sortTeamGroupsByTeamName } from "@/utils/team-sort-util";
import { MonthlyMembershipProvider } from "@/components/feature/nurse-membership/MonthlyMembershipContext";
import type { PrevTailData } from "@/hooks/roster-craete/useRosterPrevTail";
import type {
  EmergencyBulkPeriodPick,
  EmergencyPostApplyHighlight,
  EmergencyReplacementSlotPick,
} from "@/types/emergency-replacement.types";
import type { CopyNurses } from "@/utils/rosterCreate-util";

type RowMoveDirection = "up" | "down";

export default function Roster_create_Tab2({
  copyNurses,
  availableShifts,
  isEditMode,
  selectedShift,
  modDayShift,
  prevTailData,
  showPerShiftStats = false,
  cellViolationMap,
  selectIdForSideprofile,
  scheduleYear = 0,
  scheduleMonth = 0,
  schedulePayloadYear,
  schedulePayloadMonth,
  currentGroupId = null,
  isEmergencyPickMode = false,
  onEmergencySlotPick,
  emergencySlotPick = null,
  isEmergencyBulkPickMode = false,
  onEmergencyBulkPick,
  emergencyBulkPeriodPick = null,
  emergencyBulkSelectedSteps = null,
  emergencyReplacementCandidateNurseId = null,
  emergencyPostApplyHighlight = null,
  enableRowSequenceReorder = false,
  onMoveNurseRow,
}: {
  copyNurses: CopyNurses;
  availableShifts: type.availableShifts[];
  isEditMode: boolean;
  selectedShift: type.Type_availableShifts | null;
  modDayShift: (
    nurseId: string,
    scheduleDay: number,
    changeShift: string,
    cellShiftDbId?: number | null
  ) => void;
  prevTailData?: PrevTailData | null;
  showPerShiftStats?: boolean;
  /** 상위 `useRosterValidation` 결과 — 전체 보기와 동일 검증(최소인원 포함) */
  cellViolationMap: Map<string, CellViolationInfo>;
  selectIdForSideprofile?: (bool: boolean, nurseID: string) => void;
  /** `row_NurseRosterList` 파견·휴직 기간 바 — 전체 보기(TableBody)와 동일 */
  scheduleYear?: number;
  scheduleMonth?: number;
  schedulePayloadYear?: number;
  schedulePayloadMonth?: number;
  currentGroupId?: string | null;
  isEmergencyPickMode?: boolean;
  onEmergencySlotPick?: (pick: EmergencyReplacementSlotPick) => void;
  emergencySlotPick?: EmergencyReplacementSlotPick | null;
  isEmergencyBulkPickMode?: boolean;
  onEmergencyBulkPick?: (pick: {
    nurseId: string;
    nurseName: string;
    dayIndex: number;
    dateISO: string;
    shiftColor?: string;
  }) => void;
  emergencyBulkPeriodPick?: EmergencyBulkPeriodPick | null;
  emergencyBulkSelectedSteps?: Array<{ date: string; nurseId: string }> | null;
  emergencyReplacementCandidateNurseId?: string | null;
  emergencyPostApplyHighlight?: EmergencyPostApplyHighlight | null;
  enableRowSequenceReorder?: boolean;
  onMoveNurseRow?: (
    nurseId: string,
    direction: RowMoveDirection,
    visibleNurseIds: readonly string[],
    captureRowPositions?: () => void
  ) => void;
}) {
  const { curDate } = useScheduleStore();
  const isAdminHook = useIsAdmin();
  const { selectedGroupId: selectedGroupIdFromStore } = useGroupStore();

  // teamsSchema 가져오기 (팀 이름·메타용 — 멤버 배치 기준은 아래 as_of_team)
  const targetGroupId = currentGroupId ?? selectedGroupIdFromStore ?? null;
  const {
    data: teamsSchema,
    isLoading: isTeamsLoading,
    isFetching: isTeamsFetching,
  } = useTeamsQuery(
    targetGroupId,
    scheduleYear || undefined,
    scheduleMonth || undefined,
    {
      enabled: !!targetGroupId && !!scheduleYear && !!scheduleMonth,
    }
  );

  // 근무자관리(`useGroupMembers`)와 "동일 플로우" — 선택 월(year/month) 소속 + as_of_team.
  //   그룹은 부모가 이미 권위 소스(`currentGroupId` = isAdmin ? selectedGroupId : loginUser.group_id)로
  //   넘겨준다. 근무표 만들기는 특정 월 생성이므로 scheduleYear/scheduleMonth 를 그대로 사용한다.
  const { data: monthMembers } = useGroupMembers(
    currentGroupId,
    scheduleYear || undefined,
    scheduleMonth || undefined
  );

  // nurse_id → 선택 월 소속(as_of_team 포함). 근무자관리 Head_nurse_management 와 동일 idiom.
  const memberStatusMap = useMemo(
    () =>
      new Map(
        (monthMembers?.members ?? []).map((m) => [m.nurse_id, m] as const)
      ),
    [monthMembers]
  );

  const [nurseListByTeam, nurseListNoTeam] = useMemo(() => {
    if (!copyNurses) {
      return [
        [] as type.NurseListByTeam[],
        {
          teams: { team_name: "미등록", team_id: null, team_members: [] },
          nurses: [],
        } as type.NurseListByTeam,
      ];
    }
    if (!teamsSchema || teamsSchema.length === 0) {
      return [
        [] as type.NurseListByTeam[],
        {
          teams: { team_name: "미등록", team_id: null, team_members: [] },
          nurses: copyNurses,
        } as type.NurseListByTeam,
      ];
    }

    const teamByNurseId = new Map<string, type.FetchTeams>();
    const nurseIdsFromTeamEndpoint = new Set<string>();
    teamsSchema.forEach((team) => {
      team.team_members.forEach((nurseId) => {
        nurseIdsFromTeamEndpoint.add(nurseId);
        teamByNurseId.set(nurseId, team);
      });
    });

    const teamEndpointGroups = teamsSchema.map((team) => {
      const nurses = copyNurses.filter(
        (nurse) => teamByNurseId.get(nurse.nurse_id)?.team_id === team.team_id
      );

      return { teams: team, nurses };
    });
    const noTeam = {
      teams: { team_name: "미등록", team_id: null, team_members: [] },
      nurses: copyNurses.filter(
        (nurse) => !nurseIdsFromTeamEndpoint.has(nurse.nurse_id)
      ),
    } as type.NurseListByTeam;

    return [
      sortTeamGroupsByTeamName(teamEndpointGroups as type.NurseListByTeam[]),
      noTeam,
    ];
  }, [teamsSchema, copyNurses]);

  // 제출된 간호사 목록을 직접 가져오기
  const { data: submissionData } = useRosterSubmissionQuery({
    year: curDate?.year,
    month: curDate?.month,
    groupId: isAdminHook ? (selectedGroupIdFromStore ?? null) : null,
  });
  const currentSubmittedList = submissionData?.submitted_nurses ?? [];

  // useRosterValidation에 필요한 데이터 가져오기
  const { data: serverConfig } = useRosterConfig();
  const { data: shiftsData } = useShiftsQuery(
    isAdminHook ? (selectedGroupIdFromStore ?? undefined) : undefined
  );

  const { lookupMaps: shiftLookupMaps } = useShiftLookupMaps(
    availableShifts,
    shiftsData ?? null
  );

  const {
    columnShiftIds: statsColumnShiftIds,
    displayShifts: statsDisplayShifts,
  } = useRosterDisplayShiftColumns(
    copyNurses ?? [],
    availableShifts,
    serverConfig?.use_mid,
    { includeBaselineMainTemplateColumns: true }
  );

  const isPreparingTeamView =
    isTeamsLoading || (isTeamsFetching && teamsSchema == null);

  return (
    <MonthlyMembershipProvider
      memberStatusMap={memberStatusMap}
      selectedGroupId={currentGroupId}
    >
      <div className="tableBody_team_tablelist">
        {isPreparingTeamView ? (
        <div
          className="flex min-h-[360px] w-full items-center justify-center border-t border-slate-200 bg-white"
          role="status"
          aria-live="polite"
        >
          <LoadingSpiner />
        </div>
      ) : (
          <>
            <TableBody_team
              team={nurseListNoTeam.teams}
              nurses={nurseListNoTeam.nurses}
              availableShifts={availableShifts}
              shiftLookupMaps={shiftLookupMaps}
              currentSubmittedList={currentSubmittedList}
              copyNurses={copyNurses}
              isEditMode={isEditMode}
              selectedShift={selectedShift}
              modDayShift={modDayShift}
              cellViolationMap={
                cellViolationMap as unknown as Map<string, CellViolationInfo>
              }
              statsColumnShiftIds={statsColumnShiftIds}
              statsDisplayShifts={statsDisplayShifts}
              prevTailData={prevTailData}
              showPerShiftStats={showPerShiftStats}
              selectIdForSideprofile={selectIdForSideprofile}
              scheduleYear={scheduleYear}
              scheduleMonth={scheduleMonth}
              schedulePayloadYear={schedulePayloadYear}
              schedulePayloadMonth={schedulePayloadMonth}
              currentGroupId={currentGroupId}
              isEmergencyPickMode={isEmergencyPickMode}
              onEmergencySlotPick={onEmergencySlotPick}
              emergencySlotPick={emergencySlotPick}
              isEmergencyBulkPickMode={isEmergencyBulkPickMode}
              onEmergencyBulkPick={onEmergencyBulkPick}
              emergencyBulkPeriodPick={emergencyBulkPeriodPick}
              emergencyBulkSelectedSteps={emergencyBulkSelectedSteps}
              emergencyReplacementCandidateNurseId={
                emergencyReplacementCandidateNurseId
              }
              emergencyPostApplyHighlight={emergencyPostApplyHighlight}
              enableRowSequenceReorder={enableRowSequenceReorder}
              onMoveNurseRow={onMoveNurseRow}
            />

            {Array.isArray(nurseListByTeam) &&
              nurseListByTeam.map(
                (teams: type.NurseListByTeam, idx: number) => (
                  <TableBody_team
                    key={idx}
                    team={teams.teams}
                    nurses={teams.nurses}
                    copyNurses={copyNurses}
                    availableShifts={availableShifts}
                    shiftLookupMaps={shiftLookupMaps}
                    currentSubmittedList={currentSubmittedList}
                    isEditMode={isEditMode}
                    selectedShift={selectedShift}
                    modDayShift={modDayShift}
                    cellViolationMap={
                      cellViolationMap as unknown as Map<
                        string,
                        CellViolationInfo
                      >
                    }
                    statsColumnShiftIds={statsColumnShiftIds}
                    statsDisplayShifts={statsDisplayShifts}
                    prevTailData={prevTailData}
                    showPerShiftStats={showPerShiftStats}
                    selectIdForSideprofile={selectIdForSideprofile}
                    scheduleYear={scheduleYear}
                    scheduleMonth={scheduleMonth}
                    schedulePayloadYear={schedulePayloadYear}
                    schedulePayloadMonth={schedulePayloadMonth}
                    currentGroupId={currentGroupId}
                    isEmergencyPickMode={isEmergencyPickMode}
                    onEmergencySlotPick={onEmergencySlotPick}
                    emergencySlotPick={emergencySlotPick}
                    isEmergencyBulkPickMode={isEmergencyBulkPickMode}
                    onEmergencyBulkPick={onEmergencyBulkPick}
                    emergencyBulkPeriodPick={emergencyBulkPeriodPick}
                    emergencyBulkSelectedSteps={emergencyBulkSelectedSteps}
                    emergencyReplacementCandidateNurseId={
                      emergencyReplacementCandidateNurseId
                    }
                    emergencyPostApplyHighlight={emergencyPostApplyHighlight}
                    enableRowSequenceReorder={enableRowSequenceReorder}
                    onMoveNurseRow={onMoveNurseRow}
                  />
                )
              )}
          </>
        )}
      </div>
    </MonthlyMembershipProvider>
  );
}
